// ─────────────────────────────────────────────────────────────────────────────
// LIRR + Metro-North on the map: the railcar mesh, how a train moves between GPS fixes, and
// which of mesh / badge / heading arrow each display mode draws.
//
// Framework-free, like ferryModel / r160Model, so all three are unit-tested.
//
// ── Why the car is PROCEDURAL ──
// The subway and bus bodies are authored glTFs from the sibling mta-assets repo; there is
// no M7/M8/M9 there yet. A commuter railcar is a long stainless box with a window band, a
// roof and an undercarriage, which survives being built from a few extruded plan shapes
// — ~150 triangles, vertex-coloured, shared by every car. When an authored railcar lands in
// mta-assets it should replace this the way the Xcelsior replaced the bus's boxes;
// `RAIL_CAR_LENGTH_M` and the frame below are the contract it would need to keep.
//
// The frame is deck's own: +X forward, +Y left, +Z up, metres, origin at the car's centre at
// rail height. So the layer needs no roll.
//
// ── Why a train FOLLOWS ITS TRACK between fixes, when a ferry doesn't ──
// Both railroads report GPS, like NYC Ferry, but a boat is free to take any line across the
// harbour and a train is not. Gliding a train in a straight line from fix to fix cuts every
// curve — across the Sound, through Sunnyside Yard — so each fix is PROJECTED onto the
// branch's drawn track and the glide runs along that line, as does every car behind the
// lead. A fix too far from any of its branch's lines (a GPS glitch, a line we don't draw)
// falls back to the ferry's straight-line glide rather than snapping somewhere wrong.
// ─────────────────────────────────────────────────────────────────────────────

import type { VehicleDisplay } from '../../store/settings'

/** An M7 / M8 / M9 car: 85 ft over the couplers, 10.5 ft wide. */
export const RAIL_CAR_LENGTH_M = 25.9
export const RAIL_CAR_WIDTH_M = 3.2

/**
 * Cars drawn per train. Real consists run 4–12 cars; the feeds do not say how many, and a
 * 300 m train would overhang most of the platforms it is drawn at from any zoom a rider
 * reads the map at. Six reads as "a railroad train", not a subway car, next to the R160s.
 */
export const RAIL_DRAWN_CARS = 6

/** Below this zoom `optimized` draws the badge + heading arrow instead of the mesh: at 13
 *  a car is ~3 px long. */
export const RAIL_MESH_MIN_ZOOM = 14

/** Where a badge hangs when it rides over the mesh (`both`): clear of the roof. */
export const RAIL_LABEL_ALT = 12

type RGB = readonly [number, number, number]
type XY = readonly [number, number]

// Linear-ish 0–1 vertex colours (SimpleMeshLayer multiplies them by the instance colour).
const STAINLESS: RGB = [0.78, 0.8, 0.82]
const ROOF: RGB = [0.55, 0.57, 0.6]
const GLASS: RGB = [0.08, 0.1, 0.14]
const UNDERFRAME: RGB = [0.16, 0.16, 0.17]
/** The stripe mesh is authored WHITE so its instance colour — the branch's — comes through. */
const STRIPE: RGB = [1, 1, 1]

/** The attribute form SimpleMeshLayer's `mesh` prop takes directly: non-indexed triangles,
 *  one colour per vertex. */
export interface RailMesh {
  positions: { value: Float32Array; size: 3 }
  normals: { value: Float32Array; size: 3 }
  colors: { value: Float32Array; size: 3 }
}

class MeshBuilder {
  private pos: number[] = []
  private nrm: number[] = []
  private col: number[] = []

  private tri(a: readonly number[], b: readonly number[], c: readonly number[], n: readonly number[], color: RGB) {
    for (const v of [a, b, c]) {
      this.pos.push(v[0], v[1], v[2])
      this.nrm.push(n[0], n[1], n[2])
      this.col.push(color[0], color[1], color[2])
    }
  }

  /** A convex plan polygon (counter-clockwise from above) extruded from z0 to z1: walls,
   *  plus a top when `top` is given. */
  prism(poly: readonly XY[], z0: number, z1: number, side: RGB, top?: RGB) {
    for (let i = 0; i < poly.length; i++) {
      const [x0, y0] = poly[i]
      const [x1, y1] = poly[(i + 1) % poly.length]
      const len = Math.hypot(x1 - x0, y1 - y0) || 1
      const n = [(y1 - y0) / len, -(x1 - x0) / len, 0]
      const a = [x0, y0, z0], b = [x1, y1, z0], c = [x1, y1, z1], d = [x0, y0, z1]
      this.tri(a, b, c, n, side)
      this.tri(a, c, d, n, side)
    }
    if (top) {
      const up = [0, 0, 1]
      for (let i = 1; i < poly.length - 1; i++) {
        this.tri([...poly[0], z1], [...poly[i], z1], [...poly[i + 1], z1], up, top)
      }
    }
  }

  build(): RailMesh {
    return {
      positions: { value: new Float32Array(this.pos), size: 3 },
      normals: { value: new Float32Array(this.nrm), size: 3 },
      colors: { value: new Float32Array(this.col), size: 3 },
    }
  }
}

const HALF_L = RAIL_CAR_LENGTH_M / 2 - 0.35 // the gangway gap between coupled cars
const HALF_W = RAIL_CAR_WIDTH_M / 2

/** The car body's plan: a long box with its four corners chamfered, as the ends are. */
function bodyPlan(grow = 0, inset = 0): XY[] {
  const l = HALF_L + grow - inset
  const w = HALF_W + grow - inset
  const c = 0.5
  return [
    [-l, -w + c], [-l + c, -w], [l - c, -w], [l, -w + c],
    [l, w - c], [l - c, w], [-l + c, w], [-l, w - c],
  ]
}

// Heights above the rail: floor at ~1.2 m on a high-platform railroad, roof at ~4.2 m.
const Z_UNDER = 0.35
const Z_FLOOR = 1.2
const Z_SILL = 2.05
const Z_HEAD = 2.95
const Z_EAVE = 3.75
const Z_ROOF = 4.2

/** One railcar, stainless with a glazed band. Branch colour is not in it: see the stripe. */
export function buildRailCarMesh(): RailMesh {
  const m = new MeshBuilder()
  m.prism(bodyPlan(0, 0.35), Z_UNDER, Z_FLOOR, UNDERFRAME)
  m.prism(bodyPlan(), Z_FLOOR, Z_SILL, STAINLESS)
  m.prism(bodyPlan(), Z_SILL, Z_HEAD, GLASS)
  m.prism(bodyPlan(), Z_HEAD, Z_EAVE, STAINLESS, ROOF)
  // The roof's crown, narrower than the eaves, so a pitched view reads a curved roof.
  m.prism(bodyPlan(0, 0.45), Z_EAVE, Z_ROOF, ROOF, ROOF)
  return m.build()
}

/**
 * A band along the car's side at sill height, drawn by its own layer in the BRANCH's colour
 * — not a tint, for the ferry stripe's reason (a tint repaints the glazing too). Real LIRR
 * and Metro-North cars do not wear branch colours; this is the map saying which branch a
 * train is running, in the one place that reads from a pitched camera. 3 cm proud of the
 * body so the depth test never hides it behind its own wall.
 */
export function buildRailStripeMesh(): RailMesh {
  const m = new MeshBuilder()
  m.prism(bodyPlan(0.03), Z_SILL - 0.28, Z_SILL - 0.02, STRIPE)
  return m.build()
}

// ── Display mode ─────────────────────────────────────────────────────────────

export interface RailRenderMode {
  mesh: boolean
  badge: boolean
  /** The flat heading dart beside the badge — the zoomed-out stand-in for the train. */
  arrow: boolean
}

/**
 * What a railroad train draws under each of the rider's four display modes — the same
 * meanings the subway and ferry use (see VehicleDisplay in store/settings).
 */
export function railRenderMode(display: VehicleDisplay, zoom: number): RailRenderMode {
  switch (display) {
    case 'labels':
      return { mesh: false, badge: true, arrow: false }
    case 'trains':
      return { mesh: true, badge: false, arrow: false }
    case 'both':
      return { mesh: true, badge: true, arrow: false }
    default:
      return zoom >= RAIL_MESH_MIN_ZOOM
        ? { mesh: true, badge: false, arrow: false }
        : { mesh: false, badge: true, arrow: true }
  }
}

// ── Track geometry ───────────────────────────────────────────────────────────

const M_PER_DEG = 111_320

/** A drawn track line prepared for projection: flat lon/lat pairs plus cumulative metres. */
export interface RailPath {
  lon: Float64Array
  lat: Float64Array
  /** Metres from the first point to each point. */
  cum: Float64Array
  length: number
}

export function buildRailPath(coords: readonly (readonly [number, number])[]): RailPath {
  const n = coords.length
  const lon = new Float64Array(n)
  const lat = new Float64Array(n)
  const cum = new Float64Array(n)
  for (let i = 0; i < n; i++) {
    lon[i] = coords[i][0]
    lat[i] = coords[i][1]
    if (i > 0) cum[i] = cum[i - 1] + metresBetween(lon[i - 1], lat[i - 1], lon[i], lat[i])
  }
  return { lon, lat, cum, length: n ? cum[n - 1] : 0 }
}

export function metresBetween(lon0: number, lat0: number, lon1: number, lat1: number): number {
  const kx = Math.cos((((lat0 + lat1) / 2) * Math.PI) / 180)
  return Math.hypot((lon1 - lon0) * kx, lat1 - lat0) * M_PER_DEG
}

export function bearingDeg(lon0: number, lat0: number, lon1: number, lat1: number): number {
  const kx = Math.cos((((lat0 + lat1) / 2) * Math.PI) / 180)
  return ((Math.atan2((lon1 - lon0) * kx, lat1 - lat0) * 180) / Math.PI + 360) % 360
}

/** The nearest point on a path: its distance along the line, and how far off it `pt` is. */
export function projectOnPath(path: RailPath, lon: number, lat: number): { d: number; offM: number } {
  const kx = Math.cos((lat * Math.PI) / 180) * M_PER_DEG
  const ky = M_PER_DEG
  let best = { d: 0, offM: Infinity }
  for (let i = 0; i < path.lon.length - 1; i++) {
    const ax = (path.lon[i] - lon) * kx, ay = (path.lat[i] - lat) * ky
    const bx = (path.lon[i + 1] - lon) * kx, by = (path.lat[i + 1] - lat) * ky
    const dx = bx - ax, dy = by - ay
    const len2 = dx * dx + dy * dy
    const t = len2 > 0 ? Math.max(0, Math.min(1, -(ax * dx + ay * dy) / len2)) : 0
    const off = Math.hypot(ax + t * dx, ay + t * dy)
    if (off < best.offM) best = { d: path.cum[i] + t * (path.cum[i + 1] - path.cum[i]), offM: off }
  }
  return best
}

/** The point `d` metres along a path, clamped to its ends, with the line's bearing there. */
export function pointAtDistance(path: RailPath, d: number): { lon: number; lat: number; bearing: number } {
  const n = path.lon.length
  if (n < 2) return { lon: path.lon[0] ?? 0, lat: path.lat[0] ?? 0, bearing: 0 }
  const dd = Math.max(0, Math.min(path.length, d))
  // Binary search for the segment holding dd.
  let lo = 0, hi = n - 1
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1
    if (path.cum[mid] <= dd) lo = mid
    else hi = mid
  }
  const span = path.cum[hi] - path.cum[lo]
  const t = span > 0 ? (dd - path.cum[lo]) / span : 0
  return {
    lon: path.lon[lo] + (path.lon[hi] - path.lon[lo]) * t,
    lat: path.lat[lo] + (path.lat[hi] - path.lat[lo]) * t,
    bearing: bearingDeg(path.lon[lo], path.lat[lo], path.lon[hi], path.lat[hi]),
  }
}

// ── Motion between fixes ─────────────────────────────────────────────────────

/** The poll interval (config RAIL_POLL_INTERVAL_MS): one glide spans one gap. */
export const RAIL_GLIDE_S = 30
/** A fix further than this from every line of its branch is not on a line we draw. */
export const RAIL_TRACK_SNAP_M = 150
/** While a train's current line is still within this of the new fix, it stays on it —
 *  branches share track, and hopping between two overlapping lines would restart the glide
 *  from a different distance parametrisation. */
export const RAIL_TRACK_KEEP_M = 60
/** Further than this from where it is drawn, a train is re-placed rather than glided — a
 *  first sight after the app slept, or a train re-assigned across the network. */
export const RAIL_SNAP_M = 5000
/** Movement shorter than this between fixes leaves the direction of travel as it was:
 *  GPS wander at a platform must not turn the train round. */
const DIRECTION_MIN_MOVE_M = 8
/** How quickly an off-track (free) train's heading comes round. */
const FREE_HEADING_TAU_S = 1.5

export interface RailFix {
  lon: number
  lat: number
  /** Degrees clockwise from north; the backend derives it from successive fixes. */
  bearing: number
}

export interface RailGlide {
  /** The track line being followed, or null when gliding free (off every drawn line). */
  path: RailPath | null
  fromD: number
  toD: number
  /** +1 when travelling towards increasing `d`, −1 towards decreasing. */
  dir: 1 | -1
  // Free-glide endpoints (used when `path` is null).
  fromLon: number
  fromLat: number
  toLon: number
  toLat: number
  t0: number
  t1: number
  // The drawn pose now.
  d: number
  lon: number
  lat: number
  heading: number
  /** The fix this glide is heading for, so a repeated fix doesn't restart it. */
  fixKey: string
}

export function pickPath(
  paths: readonly RailPath[],
  current: RailPath | null,
  lon: number,
  lat: number,
): { path: RailPath; d: number } | null {
  if (current) {
    const p = projectOnPath(current, lon, lat)
    if (p.offM <= RAIL_TRACK_KEEP_M) return { path: current, d: p.d }
  }
  let best: { path: RailPath; d: number; off: number } | null = null
  for (const path of paths) {
    const p = projectOnPath(path, lon, lat)
    if (p.offM <= RAIL_TRACK_SNAP_M && (!best || p.offM < best.off)) best = { path, d: p.d, off: p.offM }
  }
  return best && { path: best.path, d: best.d }
}

/** The direction along `path` at `d` that best matches a compass bearing. */
export function dirFromBearing(path: RailPath, d: number, bearing: number): 1 | -1 {
  const tangent = pointAtDistance(path, d).bearing
  const diff = Math.abs(((bearing - tangent + 540) % 360) - 180)
  return diff <= 90 ? 1 : -1
}

/**
 * Advance (or start) a train's glide to `nowSec`, taking `fix` as the latest position and
 * `paths` as its branch's drawn lines. An existing glide is updated IN PLACE and returned.
 */
export function advanceRailGlide(
  g: RailGlide | undefined,
  fix: RailFix,
  paths: readonly RailPath[],
  nowSec: number,
  dtSec: number,
): RailGlide {
  const key = `${fix.lat.toFixed(6)},${fix.lon.toFixed(6)}`
  const bearing = Number.isFinite(fix.bearing) ? fix.bearing : 0

  const place = (): RailGlide => {
    const hit = pickPath(paths, null, fix.lon, fix.lat)
    const dir = hit ? dirFromBearing(hit.path, hit.d, bearing) : 1
    return {
      path: hit?.path ?? null, fromD: hit?.d ?? 0, toD: hit?.d ?? 0, dir,
      fromLon: fix.lon, fromLat: fix.lat, toLon: fix.lon, toLat: fix.lat,
      t0: nowSec, t1: nowSec, d: hit?.d ?? 0, lon: fix.lon, lat: fix.lat, heading: bearing, fixKey: key,
    }
  }
  if (!g) return settle(place(), nowSec, dtSec, bearing)

  if (key !== g.fixKey) {
    if (metresBetween(g.lon, g.lat, fix.lon, fix.lat) > RAIL_SNAP_M) return settle(Object.assign(g, place()), nowSec, dtSec, bearing)
    const hit = pickPath(paths, g.path, fix.lon, fix.lat)
    if (hit) {
      // Start from where the train is DRAWN now, projected onto the (possibly new) line, so
      // a fix arriving early or late changes the speed, never the continuity.
      const from = hit.path === g.path ? g.d : projectOnPath(hit.path, g.lon, g.lat).d
      const moved = hit.d - from
      const dir: 1 | -1 = Math.abs(moved) >= DIRECTION_MIN_MOVE_M
        ? (moved > 0 ? 1 : -1)
        : hit.path === g.path ? g.dir : dirFromBearing(hit.path, hit.d, bearing)
      Object.assign(g, { path: hit.path, fromD: from, toD: hit.d, dir })
    } else {
      g.path = null
    }
    g.fromLon = g.lon
    g.fromLat = g.lat
    g.toLon = fix.lon
    g.toLat = fix.lat
    g.t0 = nowSec
    g.t1 = nowSec + RAIL_GLIDE_S
    g.fixKey = key
  }
  return settle(g, nowSec, dtSec, bearing)
}

/** Set the drawn pose for `nowSec`. */
function settle(g: RailGlide, nowSec: number, dtSec: number, bearing: number): RailGlide {
  const span = g.t1 - g.t0
  const p = span > 0 ? Math.min(1, Math.max(0, (nowSec - g.t0) / span)) : 1
  if (g.path) {
    g.d = g.fromD + (g.toD - g.fromD) * p
    const pt = pointAtDistance(g.path, g.d)
    g.lon = pt.lon
    g.lat = pt.lat
    g.heading = g.dir === 1 ? pt.bearing : (pt.bearing + 180) % 360
  } else {
    g.lon = g.fromLon + (g.toLon - g.fromLon) * p
    g.lat = g.fromLat + (g.toLat - g.fromLat) * p
    const k = dtSec > 0 ? 1 - Math.exp(-dtSec / FREE_HEADING_TAU_S) : 1
    const turn = ((bearing - g.heading + 540) % 360) - 180
    g.heading = (g.heading + turn * k + 360) % 360
  }
  return g
}

export interface RailCarPose {
  lon: number
  lat: number
  /** Degrees clockwise from north, the way the car faces (the train's direction). */
  bearing: number
}

/**
 * Where each drawn car sits: the lead car's nose at the glide's position, every car behind
 * it laid along the same track line — so a train bends round a curve instead of sticking out
 * of it. Off-track (free) trains are laid out straight behind their heading.
 */
export function layoutRailConsist(g: RailGlide, carCount = RAIL_DRAWN_CARS, carLen = RAIL_CAR_LENGTH_M): RailCarPose[] {
  const cars: RailCarPose[] = []
  if (g.path) {
    const path = g.path
    for (let i = 0; i < carCount; i++) {
      const front = pointAtDistance(path, g.d - g.dir * carLen * i)
      const rear = pointAtDistance(path, g.d - g.dir * carLen * (i + 1))
      const chord = metresBetween(rear.lon, rear.lat, front.lon, front.lat)
      cars.push({
        lon: (front.lon + rear.lon) / 2,
        lat: (front.lat + rear.lat) / 2,
        // A car clamped against the end of its line has no chord: use the line's own bearing.
        bearing: chord > 1 ? bearingDeg(rear.lon, rear.lat, front.lon, front.lat) : g.heading,
      })
    }
    return cars
  }
  const rad = (g.heading * Math.PI) / 180
  const kx = Math.cos((g.lat * Math.PI) / 180) * M_PER_DEG
  for (let i = 0; i < carCount; i++) {
    const back = carLen * (i + 0.5)
    cars.push({
      lon: g.lon - (Math.sin(rad) * back) / kx,
      lat: g.lat - (Math.cos(rad) * back) / M_PER_DEG,
      bearing: g.heading,
    })
  }
  return cars
}
